import { useEffect, useRef, useState } from "react";

import styled from "styled-components/macro";

import { AnimatePresence, motion } from "framer-motion";

import FnrCampaignGraph from "UI/FnrCampaignGraph/FnrCampaignGraph";
import FnrHorizontalGap from "UI/commonContainers/FnrHorizontalGap";
import { inOutZoom } from "UI/motionStyles";

import { ActionButton } from "../../ActionButton";
import { useCTAContext } from "../../CTA";
import { useOnCTAClick, useOnRecurringGiftClick } from "../../stateHooks";
import PlaceholderTitle from "../PlaceholderTitle";

import GiftData from "./GiftData";

export default function CampaignStatus({
  userRecurringGiftInfo,
  userPledgeInfo,
  setAction,
  action,
  title,
  campaignInfo,
  showGraph,
  description,
}) {
  // Adding so that the component doesn't break if the campaignInfo is null (preview mode)
  let {
    pledgingOpen = false,
    userCanGive = true,
    totalRaised = 0,
    totalPledged = 0,
    campaignGoal = 0,
  } = campaignInfo || {};

  useEffect(() => {
    if (pledgingOpen && !userRecurringGiftInfo && !userPledgeInfo) {
      setAction("Make a Pledge");
    } else if (userCanGive && !userRecurringGiftInfo) {
      setAction("Set up a recurring gift");
    } else if (userCanGive && userPledgeInfo && userRecurringGiftInfo) {
      setAction("Recurrring Gift");
    }
  }, [
    pledgingOpen,
    userPledgeInfo,
    userCanGive,
    userRecurringGiftInfo,
    setAction,
  ]);
  const hasRecurringGift = !!userRecurringGiftInfo;

  const campaignStatusProps = {
    userRecurringGiftInfo,
    userPledgeInfo,
    pledgingOpen,
    userCanGive,
    action,
    title,
    graphProps: {
      goal: Number(campaignGoal),
      pledged: totalPledged,
      raised: totalRaised,
    },
    showGraph,
    description,
    hasRecurringGift,
  };

  const onCTAClick = useOnCTAClick();
  const onRecurringGiftClick = useOnRecurringGiftClick();

  return (
    <SDiv
      onClick={() => {
        if (userCanGive) {
          if (hasRecurringGift) {
            onRecurringGiftClick();
          } else {
            onCTAClick();
          }
        }
      }}
    >
      <CampaignStatusInside {...campaignStatusProps} />
    </SDiv>
  );
}

function CampaignStatusInside({
  description,
  image,
  action,
  title,
  graphProps,
  showGraph,
  userRecurringGiftInfo,
  hasRecurringGift,
}) {
  const hasGraph = showGraph && graphProps;
  const { isPreview } = useCTAContext();

  return (
    <Wrapper>
      <TopArea $addBottomMargin={hasRecurringGift && !hasGraph}>
        <TextContent
          animate={{
            marginBottom: hasGraph ? 0 : 40,
            transition: { ease: "easeInOut" },
          }}
        >
          <FnrHorizontalGap gap={0}>
            <Title>{title}</Title>
            <PlaceholderTitle show={isPreview && !title} />
          </FnrHorizontalGap>
          {description && <Description>{description}</Description>}
        </TextContent>
        {image && <ImageContent style={{ backgroundImage: `url(${image})` }} />}
      </TopArea>
      <motion.div
        animate={{
          marginBottom: showGraph ? 80 : 0,
          transition: { ease: "easeInOut" },
        }}
      >
        <AnimatePresence>
          {hasGraph && (
            <motion.div {...inOutZoom}>
              <FnrCampaignGraph {...graphProps} />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {action &&
        (hasRecurringGift ? (
          <GiftData {...userRecurringGiftInfo} />
        ) : (
          <ActionButton action={action} />
        ))}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  height: 100%;
  box-sizing: border-box;
  position: relative;
  z-index: 10;
  flex-grow: 1;
  height: 100%;
  justify-content: center;
  position: relative;
  word-break: break-word;
`;
const Title = styled.div`
  font-weight: 600;
  font-size: 20px;
`;
const Description = styled.div`
  font-weight: 400;
`;
const TextContent = styled(motion.div)`
  flex-grow: 1;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;
const ImageContent = styled.div`
  aspect-ratio: 1;
  background: #fff;
  flex-shrink: 0;
  border-radius: 12px;
  margin-top: -4px;
  margin-right: -4px;
  height: calc(100% + 8px);
  background-size: cover;
  background-position: center center;
`;

const SDiv = styled.div`
  text-decoration: none !important;
  color: inherit;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const TopArea = styled.div`
  padding: 16px;
  margin: ${({ $addBottomMargin }) =>
    $addBottomMargin ? "8px 8px 80px 8px" : "8px 8px 0px 8px"};
`;
